import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Layout } from "@/components/layout/Layout";
import { ArrowLeft, MapPin, Phone } from "lucide-react";
import { ReviewList } from "@/components/reviews/ReviewList";
import { ReviewForm } from "@/components/reviews/ReviewForm";
import { ReviewItemProps } from "@/components/reviews/ReviewItem";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

const fetchBusinessDetail = async (id: string) => {
  const { data, error } = await supabase
    .from('business_listings')
    .select('*')
    .eq('id', id)
    .single();
    
  if (error) {
    console.error("Error fetching business detail:", error);
    throw error;
  }
  
  return data;
};

const fetchBusinessReviews = async (businessId: string) => {
  const { data, error } = await supabase
    .from('business_reviews')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });
    
  if (error) {
    console.error("Error fetching business reviews:", error);
    throw error;
  }
  
  return data.map((review) => ({
    author: review.author_name,
    avatar: review.author_avatar || "",
    rating: review.rating,
    date: new Date(review.created_at),
    content: review.content,
    userId: review.user_id
  }));
};

const fetchUserReview = async (businessId: string, userId: string) => {
  if (!userId) return null;
  
  const { data, error } = await supabase
    .from('business_reviews')
    .select('*')
    .eq('business_id', businessId)
    .eq('user_id', userId)
    .single();
    
  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error("Error fetching user review:", error);
    throw error;
  }
  
  return data;
};

const BusinessDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [averageRating, setAverageRating] = useState(0);
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  
  const { data: business, isLoading: isBusinessLoading, error: businessError } = useQuery({
    queryKey: ['business', id],
    queryFn: () => fetchBusinessDetail(id as string),
    enabled: !!id,
  });
  
  const { data: reviews = [], isLoading: isReviewsLoading } = useQuery({
    queryKey: ['business-reviews', id],
    queryFn: () => fetchBusinessReviews(id as string),
    enabled: !!id,
  });
  
  const { data: userReview, isLoading: isUserReviewLoading } = useQuery({
    queryKey: ['user-review', id, user?.id],
    queryFn: () => fetchUserReview(id as string, user?.id as string),
    enabled: !!id && !!user?.id,
  });
  
  useEffect(() => {
    if (reviews.length > 0) {
      const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
      const avgRating = totalRating / reviews.length;
      setAverageRating(parseFloat(avgRating.toFixed(1)));
    } else {
      setAverageRating(0);
    }
  }, [reviews]);
  
  const submitReviewMutation = useMutation({
    mutationFn: async ({ rating, content }: { rating: number, content: string }) => {
      if (!user) {
        throw new Error("You must be logged in to submit a review");
      }
      
      const existingReview = userReview;
      
      if (existingReview) {
        const { data, error } = await supabase
          .from('business_reviews')
          .update({
            rating,
            content,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingReview.id)
          .select();
          
        if (error) {
          console.error("Error updating review:", error);
          throw error;
        }
        
        return { data, isUpdate: true };
      } else {
        const { data, error } = await supabase
          .from('business_reviews')
          .insert({
            business_id: id,
            user_id: user.id,
            author_name: userProfile?.username || "사용자",
            author_avatar: "",
            rating,
            content
          })
          .select();
          
        if (error) {
          console.error("Error submitting review:", error);
          throw error;
        }
        
        return { data, isUpdate: false };
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['business-reviews', id] });
      queryClient.invalidateQueries({ queryKey: ['user-review', id, user?.id] });
      
      toast({
        title: result.isUpdate ? "리뷰가 수정되었습니다." : "리뷰가 등록되었습니다.",
        description: "소중한 리뷰 감사합니다.",
      });
    },
    onError: (error) => {
      console.error("Review submission error:", error);
      toast({
        title: "리뷰 등록에 실패했습니다.",
        description: "다시 시도해주세요.",
        variant: "destructive",
      });
    }
  });
  
  const handleReviewSubmit = (rating: number, content: string) => {
    if (!user) {
      toast({
        title: "로그인이 필요합니다.",
        description: "리뷰를 작성하려면 로그인해주세요.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    submitReviewMutation.mutate(
      { rating, content },
      {
        onSettled: () => {
          setIsSubmitting(false);
        }
      }
    );
  };
  
  if (isBusinessLoading) {
    return (
      <Layout>
        <div className="container py-8 max-w-screen-lg text-center">
          <h1 className="text-xl font-medium mb-4">로딩 중...</h1>
        </div>
      </Layout>
    );
  }
  
  if (businessError || !business) {
    return (
      <Layout>
        <div className="container py-8 max-w-screen-lg text-center">
          <h1 className="text-2xl font-bold mb-4">업체 정보를 찾을 수 없습니다</h1>
          <Button onClick={() => navigate("/business-tutoring")}>목록으로 돌아가기</Button>
        </div>
      </Layout>
    );
  }

  const displayRating = reviews.length > 0 ? averageRating : (business.rating || 0);
  
  const ContactInfoCard = () => (
    <Card className={isMobile ? "mb-6" : "sticky top-6"}>
      <CardContent className="pt-6">
        <div className="text-xl font-bold mb-4">연락처 정보</div>
        <div className="space-y-4 mb-6">
          <div className="flex items-center">
            <Phone className="h-5 w-5 text-muted-foreground mr-3 flex-shrink-0" />
            <span className="break-words overflow-hidden text-ellipsis">{business.contact}</span>
          </div>
        </div>
        <div className="space-y-3">
          <Button className="w-full" onClick={() => window.open(business.contact.startsWith("http") ? business.contact : `tel:${business.contact}`, '_blank')}>
            연락하기
          </Button>
        </div>
      </CardContent>
    </Card>
  );
  
  return (
    <Layout>
      <div className="container py-8 max-w-screen-lg">
        <Button 
          variant="ghost" 
          className="mb-6" 
          onClick={() => navigate("/business-tutoring")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          목록으로 돌아가기
        </Button>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <img 
                src={business.author_avatar || "/placeholder.svg"} 
                alt={business.title} 
                className="w-24 h-24 rounded-full object-cover"
              />
              <div className="overflow-hidden">
                <h1 className="text-3xl font-bold break-words overflow-hidden text-ellipsis">{business.title}</h1>
                <p className="text-muted-foreground break-words overflow-hidden text-ellipsis">{business.category}</p>
                <div className="flex items-center mt-2">
                  <span className="text-primary mr-1">★</span>
                  <span className="font-medium">{displayRating}</span>
                  {reviews.length > 0 && (
                    <span className="text-sm text-muted-foreground ml-2">({reviews.length}개 리뷰)</span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 mb-6">
              {business.badges && business.badges.map((badge: string, index: number) => (
                <Badge key={index} className="break-words overflow-hidden text-ellipsis max-w-full">{badge}</Badge>
              ))}
            </div>
            
            {isMobile && <ContactInfoCard />}
            
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-4">소개</h2>
                <p className="text-muted-foreground whitespace-pre-line break-words overflow-hidden">{business.description}</p>
              </div>
              
              {business.image_url && (
                <div className="mt-6 rounded-lg overflow-hidden">
                  <img 
                    src={business.image_url} 
                    alt={business.title} 
                    className="w-full h-auto object-contain max-h-[500px]"
                  />
                </div>
              )}
              
              <div className="mt-8">
                <ReviewList reviews={reviews} title="후기" isLoading={isReviewsLoading} />
              </div>
              
              <div className="mt-6">
                <ReviewForm 
                  onSubmit={handleReviewSubmit} 
                  isSubmitting={isSubmitting} 
                  existingReview={userReview} 
                />
              </div>
            </div>
          </div>
          
          {!isMobile && (
            <div>
              <ContactInfoCard />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BusinessDetail;
